<script setup>
import { computed, onMounted, ref } from 'vue';
import { OVERVIEW_METRICS } from '../constants';
import { useToggle } from '@vueuse/core';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';

import MetricCard from './overview/MetricCard.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
import { formatDuration } from '../helpers/durationFormatHelper';
const { t } = useI18n();

const uiFlags = useMapGetter('getOverviewUIFlags');
const agentStatus = useMapGetter('agents/getAgentStatus');
const accountConversationMetric = useMapGetter('getAccountConversationMetric');
const agentTimeSummary = useMapGetter('getAgentTimeSummary');
const agents = useMapGetter('agents/getAgents');
const store = useStore();

const accounti18nKey = 'OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS';
const teams = useMapGetter('teams/getTeams');

const teamMenuList = computed(() => {
  return [
    { label: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.ALL_TEAMS'), value: null },
    ...teams.value.map(team => ({ label: team.name, value: team.id })),
  ];
});

const agentStatusMetrics = computed(() => {
  let metric = {};
  Object.keys(agentStatus.value).forEach(key => {
    const metricName = t(
      `OVERVIEW_REPORTS.AGENT_STATUS.${OVERVIEW_METRICS[key]}`
    );
    metric[metricName] = agentStatus.value[key];
  });
  return metric;
});
const conversationMetrics = computed(() => {
  let metric = {};
  Object.keys(accountConversationMetric.value).forEach(key => {
    const metricName = t(`${accounti18nKey}.${OVERVIEW_METRICS[key]}`);
    metric[metricName] = accountConversationMetric.value[key];
  });
  return metric;
});

const agentName = userId => {
  const agent = agents.value.find(a => a.id === userId);
  return agent ? agent.available_name || agent.name : '';
};

const mostActiveAgent = computed(() =>
  agentTimeSummary.value.reduce(
    (top, summary) =>
      !top || summary.online_duration > top.online_duration ? summary : top,
    null
  )
);

const agentTimeTiles = computed(() => {
  const totalOnlineDuration = agentTimeSummary.value.reduce(
    (sum, summary) => sum + (summary.online_duration || 0),
    0
  );
  const averageOnlineDuration = agentTimeSummary.value.length
    ? Math.round(totalOnlineDuration / agentTimeSummary.value.length)
    : 0;

  return [
    {
      label: t('OVERVIEW_REPORTS.AGENT_TIME_SUMMARY.TOTAL_ONLINE'),
      value: formatDuration(totalOnlineDuration, t),
    },
    {
      label: t('OVERVIEW_REPORTS.AGENT_TIME_SUMMARY.AVERAGE_PER_AGENT'),
      value: formatDuration(averageOnlineDuration, t),
    },
    {
      label: t('OVERVIEW_REPORTS.AGENT_TIME_SUMMARY.MOST_ACTIVE_AGENT'),
      value: mostActiveAgent.value
        ? agentName(mostActiveAgent.value.user_id)
        : '---',
    },
  ];
});

const selectedTeam = ref(null);
const selectedTeamLabel = computed(() => {
  const team =
    teamMenuList.value.find(
      menuItem => menuItem.value === selectedTeam.value
    ) || {};
  return team.label;
});
const fetchData = () => {
  const params = {};
  if (selectedTeam.value) {
    params.team_id = selectedTeam.value;
  }
  store.dispatch('fetchAccountConversationMetric', params);

  const now = new Date();
  store.dispatch('fetchAgentTimeSummary', {
    from: getUnixTime(startOfDay(now)),
    to: getUnixTime(endOfDay(now)),
  });
};

const { startRefetching } = useLiveRefresh(fetchData);
const [showDropdown, toggleDropdown] = useToggle();

const handleAction = ({ value }) => {
  toggleDropdown(false);
  selectedTeam.value = value;
  fetchData();
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="flex flex-col items-center md:flex-row gap-4">
    <div
      class="flex-1 w-full max-w-full md:w-[65%] md:max-w-[65%] conversation-metric"
    >
      <MetricCard
        :header="t(`${accounti18nKey}.HEADER`)"
        :is-loading="uiFlags.isFetchingAccountConversationMetric"
        :loading-message="t(`${accounti18nKey}.LOADING_MESSAGE`)"
      >
        <template v-if="teams.length" #control>
          <div
            v-on-clickaway="() => toggleDropdown(false)"
            class="relative flex items-center group z-50"
          >
            <Button
              sm
              slate
              faded
              :label="selectedTeamLabel"
              class="capitalize rounded-md group-hover:bg-n-alpha-2"
              @click="toggleDropdown()"
            />
            <DropdownMenu
              v-if="showDropdown"
              :menu-items="teamMenuList"
              class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full"
              label-class="capitalize"
              @action="handleAction($event)"
            />
          </div>
        </template>
        <div
          v-for="(metric, name, index) in conversationMetrics"
          :key="index"
          class="flex-1 min-w-0 pb-2"
        >
          <h3 class="text-base text-n-slate-11">
            {{ name }}
          </h3>
          <p class="text-n-slate-12 text-3xl mb-0 mt-1">
            {{ metric }}
          </p>
        </div>
      </MetricCard>
    </div>
    <div class="flex-1 w-full max-w-full md:w-[35%] md:max-w-[35%]">
      <MetricCard :header="$t('OVERVIEW_REPORTS.AGENT_STATUS.HEADER')">
        <div class="grid grid-cols-3 gap-x-3 gap-y-4 w-full">
          <div
            v-for="(metric, name, index) in agentStatusMetrics"
            :key="index"
            class="min-w-0"
          >
            <h3 class="text-base text-n-slate-11">
              {{ name }}
            </h3>
            <p class="text-n-slate-12 text-3xl mb-0 mt-1">
              {{ metric }}
            </p>
          </div>
          <div v-for="tile in agentTimeTiles" :key="tile.label" class="min-w-0">
            <h3 class="text-base text-n-slate-11">
              {{ tile.label }}
            </h3>
            <p class="text-n-slate-12 text-3xl mb-0 mt-1 truncate">
              {{ tile.value }}
            </p>
          </div>
        </div>
      </MetricCard>
    </div>
  </div>
</template>
